import { defineComponent as _defineComponent } from 'vue'
import { Heater, HeaterState, MachineStatus } from "@duet3d/objectmodel";
import { computed, PropType, ref } from "vue";

import i18n from "@/i18n";
import store from "@/store";
import { getHeaterColor } from "@/utils/colors";
import { displaySensorValue } from "@/utils/display";


export default /*#__PURE__*/_defineComponent({
  __name: 'HeaterRows',
  props: {
    type: String as PropType<"bed" | "chamber">
},
  emits: ["resetHeaterFault"],
  setup(__props, { emit }: { emit: ({
    (e: "resetHeaterFault", heater: number): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const disabled = computed<boolean>(() => store.getters["uiFrozen"] || [MachineStatus.pausing, MachineStatus.processing, MachineStatus.resuming].includes(store.state.machine.model.state.status));

// Settings
const singleControl = computed(() => (props.type === "bed") ? store.state.machine.settings.singleBedControl : store.state.machine.settings.singleChamberControl);

// Heater abstraction
const heaterItems = computed(() => {
    const heaterIndices = (props.type === "bed") ? store.state.machine.model.heat.bedHeaters : store.state.machine.model.heat.chamberHeaters;
    const heaterList: Array<{ index: number, heater: Heater, heaterIndex: number }> = [];
    for (let index = 0; index < heaterIndices.length; index++) {
        const heaterIndex = heaterIndices[index];
        if (heaterIndex >= 0 && heaterIndex < store.state.machine.model.heat.heaters.length) {
            const heater = store.state.machine.model.heat.heaters[heaterIndex];
            if (heater !== null) {
                heaterList.push({
                    index,
                    heater,
                    heaterIndex
                });
            }
        }
    }
    return heaterList;
});
const firstHeater = computed(() => (heaterItems.value.length > 0) ? heaterItems.value[0].heater : null);

// Single heater control
const selectedIndex = ref(-1), selectedHeater = ref<Heater | null>(null), selectedHeaterIndex = ref(-1);

function selectHeater(index: number, heater: Heater | null, heaterIndex: number) {
    selectedIndex.value = index;
    selectedHeater.value = heater;
    selectedHeaterIndex.value = heaterIndex;
}

const singleHeaterCaption = computed(() => {
    if (selectedHeater === null) {
        return (props.type === "bed") ? i18n.t("panel.tools.beds") : i18n.t("panel.tools.chambers");
    }
    return (props.type === "bed") ? i18n.t("panel.tools.bed", [""]) : i18n.t("panel.tools.chamber", [""]);
});

async function allHeatersClick() {
    if (disabled.value) {
        return;
    }

    // Get valid indices
    const heaters = (props.type === "bed") ? store.state.machine.model.heat.bedHeaters : store.state.machine.model.heat.chamberHeaters;
    const indices: Array<number> = [];
    for (let index = 0; index < heaters.length; index++) {
        const heaterIndex = heaters[index];
        if (heaterIndex >= 0 && heaterIndex < store.state.machine.model.heat.heaters.length) {
            const bedHeater = store.state.machine.model.heat.heaters[heaterIndex];
            if (bedHeater !== null) {
                indices.push(index);

                // Since there is no dedicate facility for resetting heater faults, check all bed heaters here
                if (bedHeater.state === HeaterState.fault) {
                    emit("resetHeaterFault", heaterIndex);
                    return;
                }
            }
        }
    }

    // Control heaters depending on the state of the first heater
    if (firstHeater.value !== null) {
        if (props.type === "bed") {
            switch (firstHeater.value.state) {
                case HeaterState.off:		// Off -> Active
                    await store.dispatch("machine/sendCode", indices.map(index => `M140 P${index} S${firstHeater.value!.active}`).join('\n'));
                    break;

                case HeaterState.standby:	// Standby -> Off
                    await store.dispatch("machine/sendCode", indices.map(index => `M140 P${index} S-273.15`).join('\n'));
                    break;

                case HeaterState.active:	// Active -> Standby
                    await store.dispatch("machine/sendCode", indices.map(index => `M144 P${index}\n`).join('\n'));
                    break;

                // Faults are handled before we get here
            }
        } else {
            switch (firstHeater.value.state) {
                case HeaterState.off:		// Off -> Active
                    await store.dispatch("machine/sendCode", indices.map(index => `M141 P${index} S${firstHeater.value!.active}`).join('\n'));
                    break;

                // Standby mode for chambers is not officially supported yet (there is no code for standby control)

                default:	// Active -> Off
                    await store.dispatch("machine/sendCode", indices.map(index => `M141 P${index} S-273.15`).join('\n'));
                    break;

                // Faults are handled before we get here
            }
        }
    }
}

// Individual heater control
function getHeaterClasses(heater: number) {
    const classes = [getHeaterColor(heater)];
    if (disabled.value) {
        classes.push("disabled-heater");
    }
    return classes;
}

function getHeaterName(heater: Heater | null, heaterIndex: number) {
    if ((heater !== null) && (heater.sensor >= 0) && (heater.sensor < store.state.machine.model.sensors.analog.length)) {
        const sensor = store.state.machine.model.sensors.analog[heater.sensor];
        if ((sensor !== null) && sensor.name) {
            const matches = /(.*)\[(.*)\]$/.exec(sensor.name);
            if (matches) {
                return matches[1];
            }
            return sensor.name;
        }
    }
    return i18n.t("panel.tools.heater", [heaterIndex]);
}

function getHeaterValue(heater: Heater | null) {
    if ((heater !== null) && (heater.sensor >= 0) && (heater.sensor < store.state.machine.model.sensors.analog.length)) {
        const sensor = store.state.machine.model.sensors.analog[heater.sensor];
        if (sensor !== null) {
            return displaySensorValue(sensor);
        }
    }
    return i18n.t("generic.noValue");
}

async function heaterClick(index: number, heater: Heater | null) {
    if (disabled.value || !heater) {
        return;
    }

    if (props.type === "bed") {
        switch (heater.state) {
            case HeaterState.off:		// Off -> Active
                await store.dispatch("machine/sendCode", `M140 P${index} S${heater.active}`);
                break;

            case HeaterState.standby:	// Standby -> Off
                await store.dispatch("machine/sendCode", `M140 P${index} S-273.15`);
                break;

            case HeaterState.active:	// Active -> Standby
                await store.dispatch("machine/sendCode", `M144 P${index}`);
                break;

            case HeaterState.fault:		// Fault -> Ask for reset
                emit("resetHeaterFault", store.state.machine.model.heat.heaters.indexOf(heater));
                break;
        }
    } else {
        switch (heater.state) {
            case HeaterState.off:		// Off -> Active
                await store.dispatch("machine/sendCode", `M141 P${index} S${heater.active}`);
                break;

            // Standby mode for chambers is not officially supported yet (there is no code for standby control)

            case HeaterState.fault:		// Fault -> Ask for reset
                emit("resetHeaterFault", store.state.machine.model.heat.heaters.indexOf(heater));
                break;

            default:	// Active -> Off
                await store.dispatch("machine/sendCode", `M141 P${index} S-273.15`);
                break;
        }
    }
}

return { __sfc: true,props, emit, disabled, singleControl, heaterItems, firstHeater, selectedIndex, selectedHeater, selectedHeaterIndex, selectHeater, singleHeaterCaption, allHeatersClick, getHeaterClasses, getHeaterName, getHeaterValue, heaterClick, store }
}

})